/**
 * Shared ops-cost-report fetch helpers (handler + page inject).
 * Path lib/fetch/ loads before lib/luxFetchHandlers/ in the gulp bundle.
 */
const OPS_COST_REPORT_CREATE_PATH_RE = /^\/ops-cost-reports\/create\/?$/
// Group 2 is the path after /edit. Overview and report-detail must not share a mount key.
const OPS_COST_REPORT_EDIT_PATH_RE = /^\/ops-cost-reports\/(\d+)\/edit(\/.*)?$/
const OPS_COST_REPORT_PAGE_RE = /^\/ops-cost-reports\/(?:create|(\d+)\/edit)(?:\/.*)?$/
const OPS_COST_REPORT_DETAIL_PATH_RE = /^\/ops-cost-reports\/\d+\/edit\/report-detail\/(\d+)\/?$/
const TEMPLATE_RENDER_API_PATH = '/services/pmp-document/api/v1/templates/render'
const DOCUMENTS_API_PATH = '/services/pmp-document/api/v1/documents'
const PENDING_SESSION_KEY = 'lux_ops_cost_report_pending_generating'
const OPS_COST_REPORT_API_PATH = '/services/pmp-accounting/api/v1/ops-cost-report'

/** Set on edit/create mount; used by luxFetchHandlers/opsCostReport when pathname alone is insufficient. */
let luxOpsCostReportActiveSourceId = null
/**
 * New BKA UI preview. Set from the overview row button (data-row-key / data-testid).
 * The render body has no contract id. Cleared when the editor unmounts.
 */
let luxOpsCostReportPreviewContractId = null
/** Editor save hook; set by onOpsCostReportPageMatch on mount. */
let luxOpsCostReportEditorApi = null
/** Shared Lux-side editor state (handler + page inject). */
const luxOpsCostReportFetchState = {
  lastOpenItemsAsOfDate: '',
}

function readPendingBySourceId() {
  try {
    const raw = sessionStorage.getItem(PENDING_SESSION_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function writePendingBySourceId(map) {
  sessionStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(map))
}

function markDocumentsPendingGeneration(sourceId, documentIds) {
  const map = readPendingBySourceId()
  const key = String(sourceId)
  const existing = new Set(Array.isArray(map[key]) ? map[key] : [])
  for (const id of documentIds) {
    if (Number.isFinite(id) && id > 0) existing.add(id)
  }
  map[key] = [...existing]
  writePendingBySourceId(map)
}

function getPendingDocumentIds(sourceId) {
  const map = readPendingBySourceId()
  const ids = map[String(sourceId)]
  return new Set(Array.isArray(ids) ? ids.filter((id) => Number.isFinite(id) && id > 0) : [])
}

function clearPendingDocument(sourceId, documentId) {
  const map = readPendingBySourceId()
  const key = String(sourceId)
  const ids = map[key]
  if (!Array.isArray(ids)) return
  const next = ids.filter((id) => id !== documentId)
  if (next.length === 0) {
    delete map[key]
  } else {
    map[key] = next
  }
  writePendingBySourceId(map)
}

function parseSourceIdsFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin)
    const raw = parsed.searchParams.get('sourceIds')
    if (!raw) return []
    return raw.split(',').map((part) => Number.parseInt(part.trim(), 10)).filter((id) => Number.isFinite(id))
  } catch {
    const match = url.match(/[?&]sourceIds=([^&]+)/)
    if (!match) return []
    return match[1].split(',').map((part) => Number.parseInt(part.trim(), 10)).filter((id) => Number.isFinite(id))
  }
}

function isOpsCostReportDocumentsListGet(url, method, sourceId) {
  if (method !== 'GET') return false
  if (!url.includes(DOCUMENTS_API_PATH)) return false
  if (url.includes('/documents/template')) return false
  const sourceIds = parseSourceIdsFromUrl(url)
  return sourceIds.includes(sourceId)
}

/** Create batch only. Template, upload and document-by-id stay on their own paths. */
function isOpsCostReportDocumentsCreatePost(url, method) {
  if (method !== 'POST') return false
  try {
    const pathname = new URL(url, window.location.origin).pathname.replace(/\/$/, '')
    return pathname === DOCUMENTS_API_PATH
  } catch {
    return false
  }
}

function isOpsCostReportCreateEntry(entry) {
  return !!entry && typeof entry === 'object' && entry.sourceType === 'OPS_COST_REPORT' && Number(entry.sourceId) > 0
}

function opsCostDocumentHasFile(doc) {
  return typeof doc?.url === 'string' && doc.url.trim() !== ''
}

function patchDocumentForLuxGeneration(doc, pendingIds, sourceId) {
  if (!doc || typeof doc !== 'object') return doc
  const id = Number(doc.id)
  if (!Number.isFinite(id) || !pendingIds.has(id)) return doc

  const generation = doc.generationState
  const removed = doc.state === 'DELETED' || doc.state === 'VOID'
  // Impower can mark an empty shell READY when pdfData was stripped. That READY
  // has no file, so it stays overlaid. FAILED is Lux giving up (updateState,
  // no url) and must end the overlay, otherwise later GETs keep showing GENERATING.
  const readyWithFile = generation === 'READY' && opsCostDocumentHasFile(doc)
  if (readyWithFile || generation === 'FAILED' || removed) {
    clearPendingDocument(sourceId, id)
    return doc
  }

  return { ...doc, generationState: 'GENERATING' }
}

function patchDocumentsPayload(data, sourceId, pendingIds) {
  if (!data || pendingIds.size === 0) return data

  if (Array.isArray(data.content)) {
    return {
      ...data,
      content: data.content.map((doc) => patchDocumentForLuxGeneration(doc, pendingIds, sourceId)),
    }
  }

  if (data.id != null && Number.isFinite(Number(data.id))) {
    return patchDocumentForLuxGeneration(data, pendingIds, sourceId)
  }

  if (Array.isArray(data)) {
    return data.map((doc) => patchDocumentForLuxGeneration(doc, pendingIds, sourceId))
  }

  return data
}

async function patchDocumentsGetResponse(response, sourceId, pendingIds) {
  if (!response.ok || pendingIds.size === 0) return response

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) return response

  try {
    const data = await response.clone().json()
    const patched = patchDocumentsPayload(data, sourceId, pendingIds)
    if (patched === data) return response

    const headers = new Headers(response.headers)
    headers.set('content-type', 'application/json')
    return new Response(JSON.stringify(patched), {
      status: response.status,
      statusText: response.statusText,
      headers,
    })
  } catch {
    return response
  }
}

async function enqueueOpsCostReportGeneration(sourceId, documentIds) {
  let token = null

  try {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    token = auth?.token || null
  } catch {
    token = null
  }

  if (!token) {
    throw new Error('no auth token in localStorage')
  }

  const doFetch = window.luxNativeFetch || window.fetch
  const response = await doFetch('/api/ops-costs-report/enqueue', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'same-origin',
    body: JSON.stringify({ sourceId, documentIds }),
  })

  if (!response.ok) {
    let detail = ''
    try {
      detail = (await response.json())?.error || ''
    } catch {
      detail = ''
    }
    throw new Error(detail || `enqueue failed: ${response.status}`)
  }
}

function parseReportIdFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin)
    const match = parsed.pathname.match(/\/ops-cost-report\/(\d+)$/)
    const id = match ? Number(match[1]) : NaN
    return Number.isFinite(id) && id > 0 ? id : null
  } catch {
    const match = String(url).match(/\/ops-cost-report\/(\d+)(?:\?|$)/)
    const id = match ? Number(match[1]) : NaN
    return Number.isFinite(id) && id > 0 ? id : null
  }
}

function isOpsCostReportSaveRequest(url, method) {
  const upperMethod = (method || 'GET').toUpperCase()
  if (upperMethod !== 'POST' && upperMethod !== 'PUT') return false
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.pathname === OPS_COST_REPORT_API_PATH) return true
    if (upperMethod === 'PUT' && parseReportIdFromUrl(url)) return true
    return false
  } catch {
    if (!url.includes(OPS_COST_REPORT_API_PATH)) return false
    if (url.match(/\/ops-cost-report\/\d+/)) return upperMethod === 'PUT'
    return !url.includes('?')
  }
}

function isOpsCostReportPage(pathname = window.location.pathname) {
  return OPS_COST_REPORT_PAGE_RE.test(pathname)
}

function getUiRouteKey(pathname = window.location.pathname) {
  if (OPS_COST_REPORT_CREATE_PATH_RE.test(pathname)) return 'create'
  const match = pathname.match(OPS_COST_REPORT_EDIT_PATH_RE)
  if (!match) return null
  // Impower replaces the editor form on report-detail. Include the suffix so the Lux form remounts.
  const suffix = (match[2] || '').replace(/\/$/, '')
  return suffix ? `edit:${match[1]}:${suffix}` : `edit:${match[1]}`
}

function parseEditReportIdFromPathname(pathname = window.location.pathname) {
  const match = pathname.match(OPS_COST_REPORT_EDIT_PATH_RE)
  const id = match ? Number.parseInt(match[1], 10) : NaN
  return Number.isFinite(id) && id > 0 ? id : null
}

function parsePreviewContractIdFromPathname(pathname = window.location.pathname) {
  const match = pathname.match(OPS_COST_REPORT_DETAIL_PATH_RE)
  const id = match ? Number.parseInt(match[1], 10) : NaN
  return Number.isFinite(id) && id > 0 ? id : null
}

/** Detail URL wins over a click on another row. */
function resolveOpsCostReportPreviewContractId(pathname = window.location.pathname) {
  return parsePreviewContractIdFromPathname(pathname) ?? luxOpsCostReportPreviewContractId
}

function rememberOpsCostReportPreviewContractId(unitContractId) {
  const id = Number(unitContractId)
  luxOpsCostReportPreviewContractId = Number.isFinite(id) && id > 0 ? id : null
}

/**
 * New BKA UI preview only. Returns a Lux PDF response. Does not enqueue.
 * Caller falls through to Impower when this throws.
 */
async function fetchOpsCostReportPreviewPdf(sourceId, unitContractId) {
  let token = null
  try {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    token = auth?.token || null
  } catch {
    token = null
  }
  if (!token) {
    throw new Error('no auth token in localStorage')
  }

  const doFetch = window.luxNativeFetch || window.fetch
  const response = await doFetch('/api/ops-costs-report/preview', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'same-origin',
    body: JSON.stringify({ sourceId, unitContractId }),
  })

  if (!response.ok) {
    let detail = ''
    try {
      detail = (await response.json())?.error || ''
    } catch {
      detail = ''
    }
    throw new Error(detail || `preview failed: ${response.status}`)
  }

  const blob = await response.blob()
  const headers = new Headers()
  headers.set('Content-Type', 'application/pdf')
  return new Response(blob, { status: 200, headers })
}

/** Edit pathname, active mount id, or pending sessionStorage + sourceIds on document GET. */
function resolveOpsCostReportSourceId(url, method) {
  const fromPath = parseEditReportIdFromPathname()
  if (fromPath) return fromPath
  if (luxOpsCostReportActiveSourceId) return luxOpsCostReportActiveSourceId

  if (method !== 'GET' || !url.includes(DOCUMENTS_API_PATH) || url.includes('/documents/template')) {
    return null
  }

  const pendingMap = readPendingBySourceId()
  const pendingKeys = Object.keys(pendingMap).filter(
    (key) => Array.isArray(pendingMap[key]) && pendingMap[key].length > 0,
  )
  if (pendingKeys.length === 0) return null

  for (const id of parseSourceIdsFromUrl(url)) {
    if (pendingKeys.includes(String(id))) return id
  }

  return null
}
